import { ComponentPropsWithoutRef, MouseEventHandler, ReactNode } from "react";
import { DialogBody, DialogFrame, DialogFooter } from "./DialogFrame.tsx";
import ErrorBoundary from "./ErrorBoundary.tsx";
import BaseLinkOrButton from "./BaseLinkOrButton.tsx";
import LoaderButton from "./LoaderButton.tsx";
import { useIsFullScreenDialog } from "./dialogFrameHelpers.ts";

interface FooterButton {
  label: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  href?: string;
  disabled?: boolean;
}

type PrimaryFooterButton = FooterButton &
  Partial<
    Pick<
      ComponentPropsWithoutRef<typeof LoaderButton>,
      "loading" | "loadingContext"
    >
  >;

interface DialogProps extends ComponentPropsWithoutRef<typeof DialogFrame> {
  primaryButton?: PrimaryFooterButton;
  secondaryButton?: FooterButton;
  centerButtons?: boolean;
  disableFullScreen?: boolean;
}

function PrimaryButton({
  loading,
  loadingContext,
  onClick,
  href,
  label,
  disabled,
}: PrimaryFooterButton) {
  const commonButtonProps = {
    className: "vst-button-rectangle",
    onClick,
    disabled,
    children: label,
  };

  if (typeof loading === "boolean") {
    return (
      <LoaderButton
        {...commonButtonProps}
        loading={loading}
        loadingContext={loadingContext}
      >
        {label}
      </LoaderButton>
    );
  }

  return <BaseLinkOrButton {...commonButtonProps} href={href} />;
}

export const Dialog = ({
  primaryButton,
  secondaryButton,
  centerButtons,
  children,
  disableFullScreen = false,
  ...frameProps
}: DialogProps) => {
  const isSmallScreen = useIsFullScreenDialog();
  const fullScreen = !disableFullScreen && isSmallScreen;

  return (
    <DialogFrame {...frameProps} fullScreen={fullScreen}>
      <ErrorBoundary>
        <DialogBody>{children}</DialogBody>
        {primaryButton && (
          <DialogFooter center={centerButtons} reverseButtons={fullScreen}>
            {secondaryButton && (
              <button
                key="secondary"
                className="vst-button-rectangle vst-button-variant-naked"
                onClick={secondaryButton.onClick}
                type="button"
                disabled={secondaryButton?.disabled}
              >
                {secondaryButton.label}
              </button>
            )}
            <PrimaryButton key="primary" {...primaryButton} />
          </DialogFooter>
        )}
      </ErrorBoundary>
    </DialogFrame>
  );
};
